'use client';

import { createContext, useCallback, useMemo, useContext } from 'react';
import type { PropsWithChildren } from 'react';
import { lessThan, type Money } from '@ifixit/helpers';
import { getPriceTier } from './price-tier';

type AppContext = {
   ifixitOrigin: string;
   adminMessage?: string;
   priceTiers: Record<string, string | null>;
   indexPrefix?: string;
   isLoggedIn: boolean;
   language?: string;
   isOnMobileApp: boolean;
   storeLocale: string;
};

const AppContext = createContext<AppContext | null>(null);

const { Provider } = AppContext;

type AppProviderProps = PropsWithChildren<AppContext>;

export function AppProvider({
   ifixitOrigin,
   isLoggedIn,
   adminMessage,
   priceTiers,
   children,
   indexPrefix,
   language,
   isOnMobileApp,
   storeLocale,
}: AppProviderProps) {
   const value = useMemo(
      (): AppContext => ({
         ifixitOrigin,
         isLoggedIn,
         adminMessage,
         priceTiers,
         indexPrefix,
         language,
         isOnMobileApp,
         storeLocale,
      }),
      [
         ifixitOrigin,
         isLoggedIn,
         adminMessage,
         priceTiers,
         indexPrefix,
         language,
         isOnMobileApp,
         storeLocale,
      ]
   );

   return <Provider value={value}>{children}</Provider>;
}

export function useAppContext() {
   const context = useContext(AppContext);
   if (context === null) {
      // eslint-disable-next-line @ifixit/no-new-error
      throw new Error('useAppContext must be used within a AppProvider');
   }
   return context;
}

export function usePriceTier({ storeCode }: { storeCode: string }) {
   const { priceTiers } = useAppContext();
   return getPriceTier({ priceTiers, storeCode });
}

export type UseUserPriceProps = {
   price: Money;
   compareAtPrice?: Money | null;
   proPricesByTier?: Record<string, Money> | null;
};

type UseUserPriceResult = {
   price: Money;
   compareAtPrice?: Money | null;
   isProPrice: boolean;
};

enum PriceTier {
   Employee = 'employee',
   Pro1 = 'pro_1',
   Pro2 = 'pro_2',
   Pro3 = 'pro_3',
   Pro4 = 'pro_4',
}

export function useUserPrice({
   storeCode,
   ...props
}: { storeCode: string } & UseUserPriceProps): UseUserPriceResult {
   const getUserPrice = useGetUserPrice({ storeCode });
   return getUserPrice(props);
}

export function useGetUserPrice({ storeCode }: { storeCode: string }) {
   const { priceTier, isProUser } = usePriceTier({ storeCode });

   return useCallback(
      ({ price, compareAtPrice, proPricesByTier }: UseUserPriceProps): UseUserPriceResult => {
         const proPrice = priceTier && proPricesByTier ? proPricesByTier[priceTier] || null : null;

         if (proPrice == null || lessThan(price, proPrice)) {
            return {
               price,
               compareAtPrice,
               isProPrice: false,
            };
         }
         return {
            price: proPrice,
            compareAtPrice: compareAtPrice ?? price,
            isProPrice: priceTier !== PriceTier.Employee,
         };
      },
      [priceTier, isProUser]
   );
}
